import React, {useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import {
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import Button from "@mui/material/Button";
import { useSelector } from "react-redux";

import CustomTextInput from "../CustomTextInput";
import {
  ALLOWANCE,
  CHARGE,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_TAX_SCHEME,
} from "../../constants/defaults";
import { currencyFormatter } from "../../utils/currencyFormat";
import TaxCategoryFields from "../TaxCategoryField";
import { lineItemCalculation } from "../../utils/pricingEngineForDocuments";
import AddAllowanceChargeModal from "./AddAllowanceChargeModal";
import AllowanceChargeSection from "./AllowanceChargeSection";
import UOMSelect from "./UOMSelect";
import ProductSelect from "./ProductSelect";
import { getLineItemValidationSchema } from "../../validations/LineItemValidation";
import { yupResolver } from "@hookform/resolvers/yup";
import ItemTypeSelect from "./ItemTypeSelect";
import NatureCodeSelect from "./NatureCodeSelect";
import CommodityCodeSelect from "./CommodityCodeSelect";
import ItemClassificationCodeSelect from "./ItemClassificationCodeSelect";
import ItemClassificationSchemeIDSelect from "./ItemClassificationSchemeIDSelect";
import {
  getCountryOptionByCode,
  getCountryOptions,
  getUOMOptionByCode,
  getUOMOptions,
} from "../../api/codelist/codeListsSlice";
import CountrySelect from "./CountrySelect";
import {isAnyProfitMarginTransactionType} from "../../resources/common/isProfitMarginTransactionType";
import ProfitMarginItemTypeSelect from "./ProfitMarginItemTypeSelect";
import {isImportOfGoodsTransactionType} from "../../resources/common/isImportOfGoodsTransactionType";

const initialState = {
  quantity: 1,
  unit_code: null,
  description: "",
  name: "",
  classified_tax_category: {
    id: null,
    percent: 0,
    tax_exemption_reason_code: null,
    tax_exemption_reason: null,
    zero_rated_reason_code: null,
    zero_rated_reason: null,
    tax_scheme: DEFAULT_TAX_SCHEME,
  },
  price: {
    base_amount: 1,
    base_quantity: 1,
    allowance: {
      amount: 0,
      base_amount: 1,
      reason_code: null,
      reason: null,
      taxCategory: null,
    },
  },
  allowances: [],
  charges: [],
  additional_item_identification: {
    nature_code: "",
  },
  item_specification_document_reference: {
    commodity_code: "",
    item_type: "G",
  },
  commodity_classification: {
    item_classification_code: "",
    item_classification_list_id: "HS",
    item_classification_list_version_id: "1.0",
  },
  additional_item_property: {
    attribute_name: "",
    attribute_value: "",
    name_code: ""
  }
};

const LineItemForm = ({
  submitForm,
  defaultState,
  readOnly = false,
  edit = false,
  transactionType = null,
}) => {
  const validationSchema = useMemo(
    () => getLineItemValidationSchema({}, transactionType),
    [transactionType],
  );
  const uomOption = useSelector(getUOMOptionByCode(defaultState?.unit_code));
  const countryCode = useSelector(
    getCountryOptionByCode(defaultState?.origin_country?.identification_code),
  );

  const defaultValues = defaultState
    ? {
        ...defaultState,
        allowances: defaultState?.allowances?.length
          ? defaultState?.allowances
          : [],
        charges: defaultState?.charges?.length ? defaultState?.charges : [],
        unit_code: uomOption ?? null,
        origin_country: {
          identification_code: countryCode,
        },
        additional_item_property: {
            attribute_name: defaultState?.additional_item_property?.attribute_name ?? null,
            attribute_value: defaultState?.additional_item_property?.attribute_value ?? null,
            name_code: defaultState?.additional_item_property?.name_code ?? null,
        },
        classified_tax_category: defaultState?.classified_tax_category,
        price: defaultState?.price
          ? {
              base_amount:
                defaultState.price.base_amount ||
                defaultState.price?.allowance?.base_amount ||
                1,
              base_quantity: defaultState.price.base_quantity || 1,
              allowance: {
                amount: defaultState.price?.allowance?.amount,
                base_amount: defaultState.price?.allowance?.base_amount || 1,
                reason_code: defaultState.price?.allowance?.reason_code,
                reason: defaultState.price?.allowance?.reason,
                taxCategory: defaultState.price?.allowance?.taxCategory,
              },
            }
          : initialState.price,
      }
    : initialState;
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    fields: allowanceFields,
    append: appendAllowance,
    remove: removeAllowance,
    update: updateAllowance,
  } = useFieldArray({
    control,
    name: "allowances",
  });

  const {
    fields: chargeFields,
    append: appendCharge,
    remove: removeCharge,
    update: updateCharge,
  } = useFieldArray({
    control,
    name: "charges",
  });

  const [commodityData, setCommodityData] = useState(null);
  const [natureCodeData, setNatureCodeData] = useState(null);
  const [itemType, setItemType] = useState(null);
  // Modal state
  const [allowanceModalOpen, setAllowanceModalOpen] = useState(false);
  const [allowanceType, setAllowanceType] = useState(ALLOWANCE);
  const [editingAllowanceIndex, setEditingAllowanceIndex] = useState();

  const lineItemData = useWatch({ control });
  const pricingEngine = lineItemCalculation(lineItemData);

  const isProfitMargin = isAnyProfitMarginTransactionType(transactionType);

  const handleAdd = () => {
    const data = pricingEngine;
    submitForm({
      ...data,
      origin_country: {
        identification_code:
          typeof data.origin_country?.identification_code === "string"
            ? data.origin_country?.identification_code
            : data.origin_country?.identification_code?.code,
      },
      unit_code: data.unit_code?.code,
      additional_item_property:{
        attribute_name: data.additional_item_property?.attribute_name ?? null,
        attribute_value: data.additional_item_property?.attribute_value ?? null,
        name_code: data.additional_item_property?.name_code ?? null,
      }
    });
    reset(initialState);
  };

  const handleReset = () => {
    reset(defaultValues);
  };

  const handleAddAllowance = (allowance) => {
    if (allowanceType === ALLOWANCE) {
      if (editingAllowanceIndex !== undefined) {
        updateAllowance(editingAllowanceIndex, allowance);
        setEditingAllowanceIndex(undefined);
      } else {
        appendAllowance(allowance);
      }
    } else {
      if (editingAllowanceIndex !== undefined) {
        updateCharge(editingAllowanceIndex, allowance);
        setEditingAllowanceIndex(undefined);
      } else {
        appendCharge(allowance);
      }
    }
  };

  const handleOpenDiscountModal = () => {
    setAllowanceType(ALLOWANCE);
    setEditingAllowanceIndex(undefined);
    setAllowanceModalOpen(true);
  };

  const handleOpenChargeModal = () => {
    setAllowanceType(CHARGE);
    setEditingAllowanceIndex(undefined);
    setAllowanceModalOpen(true);
  };

  const handleEditAllowance = (index, type) => {
    setAllowanceType(type);
    setEditingAllowanceIndex(index);
    setAllowanceModalOpen(true);
  };

  const handleDeleteAllowance = (index, type) => {
    if (type === ALLOWANCE) {
      removeAllowance(index);
    } else {
      removeCharge(index);
    }
  };

  const uomOptions = useSelector(getUOMOptions);
  const countryOptions = useSelector(getCountryOptions);

  const onProductSelect = (product) => {
    if (!product || !product.id) {
      if (product?.name) {
        setValue("name", product.name);
      }
      return;
    }

    setValue("name", product?.name || "");
    setValue("description", product?.description || "");
    setValue("price.base_amount", product?.unit_price ?? 1);
    setValue("quantity", product?.quantity || 1);

    const uomObj = uomOptions.find((u) => u.code === product?.unit_of_measure);
    setValue("unit_code", uomObj || null);

    const taxCategory = product?.classified_tax_category;
    setValue("classified_tax_category.id", taxCategory?.id ?? null);
    setValue("classified_tax_category.percent", taxCategory?.percent ?? 0);
    setValue(
      "classified_tax_category.tax_scheme",
      taxCategory?.tax_scheme ?? DEFAULT_TAX_SCHEME,
    );
    setValue(
      "classified_tax_category.tax_exemption_reason_code",
      taxCategory?.tax_exemption_reason_code ?? null,
    );
    setValue(
      "classified_tax_category.tax_exemption_reason",
      taxCategory?.tax_exemption_reason ?? null,
    );
    setValue(
      "classified_tax_category.zero_rated_reason_code",
      taxCategory?.zero_rated_reason_code ?? null,
    );
    setValue(
      "classified_tax_category.zero_rated_reason",
      taxCategory?.zero_rated_reason ?? null,
    );

    setValue(
      "additional_item_identification.nature_code",
      product?.additional_item_identification?.nature_code || "",
    );
    setValue(
      "item_specification_document_reference.commodity_code",
      product?.item_specification_document_reference?.commodity_code || "",
    );
    setValue(
      "item_specification_document_reference.item_type",
      product?.item_specification_document_reference?.item_type || "G",
    );
    const productItemType =
      product?.item_specification_document_reference?.item_type || "G";
    setValue(
      "commodity_classification.item_classification_code",
      product?.commodity_classification?.item_classification_code || "",
    );
    setValue(
      "commodity_classification.item_classification_list_id",
      productItemType === "S"
        ? "MP"
        : product?.commodity_classification?.item_classification_list_id ||
            "HS",
    );
    setValue(
      "commodity_classification.item_classification_list_version_id",
      product?.commodity_classification?.item_classification_list_version_id ||
        "1.0",
    );
    const countryCode = product?.origin_country?.identification_code;
    if (countryCode) {
      const countryObj = countryOptions.find((c) => c.code === countryCode);
      setValue("origin_country.identification_code", countryObj ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const onNameChange = (name) => {
    setValue("name", name || "");
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: "1rem", overflow: "auto" }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <ProductSelect
                data-testid="name-input"
                name="name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label="Item Name"
                onProductSelect={onProductSelect}
                onNameChange={onNameChange}
              />
            </Grid>
            <Grid size={6}>
              <CustomTextInput
                data-testid="description-input"
                name="description"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label="Description"
              />
            </Grid>
            <Grid size={4}>
              <UOMSelect
                control={control}
                errors={errors}
                name="unit_code"
                readOnly={readOnly}
              />
            </Grid>
            <Grid size={4}>
              <CustomTextInput
                control={control}
                data-testid="quantity-input"
                name="quantity"
                label="Quantity"
                type="number"
                errors={errors}
                readOnly={readOnly}
              />
            </Grid>
            <Grid size={4}>
              <CustomTextInput
                control={control}
                data-testid="unit-price-input"
                name="price.base_amount"
                label="Unit Price"
                type="number"
                errors={errors}
                readOnly={readOnly}
              />
            </Grid>
            <Grid size={4}>
              <NatureCodeSelect
                control={control}
                errors={errors}
                name="additional_item_identification.nature_code"
                readOnly={readOnly}
                helperText=""
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name="item_specification_document_reference.commodity_code"
                control={control}
                render={({ field }) => (
                  <CommodityCodeSelect
                    control={control}
                    errors={errors}
                    name="item_specification_document_reference.commodity_code"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name="item_specification_document_reference.item_type"
                control={control}
                render={({ field }) => (
                  <ItemTypeSelect
                    control={control}
                    errors={errors}
                    name="item_specification_document_reference.item_type"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                      setValue(
                        "commodity_classification.item_classification_list_id",
                        newCode === "S" ? "MP" : "HS",
                      );
                      setItemType(newCode);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name="commodity_classification.item_classification_list_id"
                control={control}
                render={({ field }) => (
                  <ItemClassificationSchemeIDSelect
                    control={control}
                    errors={errors}
                    name="commodity_classification.item_classification_list_id"
                    readOnly={readOnly}
                    helperText=""
                    allowedCodes={
                      lineItemData?.item_specification_document_reference
                        ?.item_type === "S"
                        ? ["MP"]
                        : ["HS"]
                    }
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                  />
                )}
              />
            </Grid>
            {itemType !== "S" && (
                <Grid size={4}>
                  <ItemClassificationCodeSelect
                      control={control}
                      errors={errors}
                      name="commodity_classification.item_classification_code"
                      readOnly={readOnly}
                      helperText=""
                  />
                </Grid>
            )}
            {isImportOfGoodsTransactionType(transactionType) && (
                <Grid size={4}>
              <Controller
                name="origin_country.identification_code"
                control={control}
                render={({ field }) => (
                  <CountrySelect
                    control={control}
                    label="Origin Country"
                    errors={errors}
                    name="origin_country.identification_code"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                  />
                )}
              />
            </Grid>)}
            {isProfitMargin &&
            <>
            <Grid size={4}>
              <CustomTextInput
                  control={control}
                  name="additional_item_property.attribute_name"
                  label="Item Attribute Name"
                  errors={errors}
                  readOnly={readOnly}
              />

            </Grid>
            <Grid size={4}>
              <CustomTextInput
                  control={control}
                  name="additional_item_property.attribute_value"
                  label="Item Attribute Value"
                  errors={errors}
                  readOnly={readOnly}
              />
            </Grid>
                <Grid size={4}>
                            <ProfitMarginItemTypeSelect
                                control={control}
                                errors={errors}
                                name="additional_item_property.name_code"
                                readOnly={readOnly}
                                helperText=""
                            />
                </Grid>

            </>}
          </Grid>
          <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
            Allowances & Charges
            <Divider />
          </Typography>

          <AllowanceChargeSection
            data-testid="charges-section"
            title="Charges"
            type={CHARGE}
            fields={chargeFields}
            onAdd={handleOpenChargeModal}
            onEdit={handleEditAllowance}
            onDelete={handleDeleteAllowance}
            readOnly={readOnly}
          />

          <AllowanceChargeSection
            data-testid="allowances-section"
            title="Allowances (Discounts)"
            type={ALLOWANCE}
            fields={allowanceFields}
            onAdd={handleOpenDiscountModal}
            onEdit={handleEditAllowance}
            onDelete={handleDeleteAllowance}
            readOnly={readOnly}
          />

          <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
            Taxes
            <Divider />
          </Typography>

          <Grid container spacing={2} mt={1}>
            <TaxCategoryFields
              data-testid="tax-category-fields"
              control={control}
              errors={errors}
              taxCategoryName="classified_tax_category.id"
              taxRateName="classified_tax_category.percent"
              setValue={setValue}
              getValues={getValues}
              clearErrors={clearErrors}
              taxCategoryReadOnly={readOnly}
              taxRateReadOnly={readOnly}
            />
          </Grid>
        </Box>

        <Divider sx={{ py: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <List sx={{ width: "50%" }}>
            <ListItem>
              <ListItemText data-testid="base-amount" primary="Base amount" />
              <Typography variant="body1">
                {currencyFormatter().format(pricingEngine.base_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText data-testid="calculated-charge" primary="Charge" />
              <Typography variant="body1">
                {currencyFormatter().format(pricingEngine.charge_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText
                data-testid="calculated-allowance"
                primary="Allowance"
              />
              <Typography variant="body1" color="error">
                -{currencyFormatter().format(pricingEngine.allowance_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText
                data-testid="calculated-total-before-tax"
                primary="Total before tax"
              />
              <Typography variant="body1">
                {currencyFormatter().format(pricingEngine.net_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText data-testid="calculated-tax" primary="Tax" />
              <Typography variant="body1">
                {currencyFormatter().format(pricingEngine.tax_amount)}
              </Typography>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "h6" }}
                data-testid="calculated-sub-total"
                primary="Sub Total"
              />
              <Typography
                variant="h6"
                color={pricingEngine.total_amount < 0 ? "error" : "inherit"}
              >
                {currencyFormatter().format(pricingEngine.total_amount)}
              </Typography>
            </ListItem>
          </List>
        </Box>
      </Paper>

      <Divider />

      <Stack direction="row" alignItems="center" spacing={1} p={2}>
        <Box flexGrow={1}>
          {pricingEngine.total_amount < 0 && (
            <Typography variant="body2" color="error" fontWeight={500}>
              Cannot add line item with negative Sub total.
            </Typography>
          )}
        </Box>

        {!readOnly && (
          <Stack direction="row" spacing={1}>
            <Button
              data-testid="reset-button"
              type="button"
              variant="outlined"
              color="primary"
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              data-testid="add-button"
              type="button"
              variant="contained"
              color="primary"
              disabled={pricingEngine.total_amount < 0}
              onClick={handleSubmit(handleAdd)}
            >
              {edit ? "Update" : "Add"}
            </Button>
          </Stack>
        )}
      </Stack>

      <AddAllowanceChargeModal
        open={allowanceModalOpen}
        onClose={() => setAllowanceModalOpen(false)}
        onAdd={handleAddAllowance}
        type={allowanceType}
        initialFormData={
          editingAllowanceIndex !== undefined
            ? allowanceType === ALLOWANCE
              ? getValues(`allowances.${editingAllowanceIndex}`)
              : getValues(`charges.${editingAllowanceIndex}`)
            : undefined
        }
        editIndex={editingAllowanceIndex}
        currentNetAmount={pricingEngine.net_amount}
        itemBaseAmount={pricingEngine.base_amount}
      />
    </>
  );
};

export default LineItemForm;
